/** @jsxImportSource theme-ui */
import React from "react";
import { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { globalHistory as history } from "@reach/router";
import { Box, Flex, Link as Href, Image, Text } from "@theme-ui/components";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { Link, graphql, useStaticQuery } from "gatsby";
import Menu, {
  Divider,
  Item as MenuItem,
  SubMenu,
  ItemGroup as MenuItemGroup,
} from "rc-menu";
import { slide as SlideMenu } from "react-burger-menu";
import { useSelector } from "react-redux";
import { uid } from "react-uid";
import "../styles/menu.less";
import { Button, NavLink, RegionalLink } from "./atoms";
import { Accordion } from "./organisms";
import { colors, typography } from "../styles/theme";
import { ListCard } from "./molecules";
import crossIcon from "../../static/logos/cross-menu.svg";
import burgerMenuIcon from "../../static/logos/burger-menu.svg";
import logo from "../../static/logos/greythr-logo.svg";
import { ChevronDown, ChevronRight } from "lucide-react";
import newFeatureGIF from "../../static/home/newFeature-MenuItem.gif"
import { useBreakpointIndex } from "@theme-ui/match-media";

const collapseNode = () => ({ opacity: 0 });
const expandNode = (node) => ({ opacity: 1 });

const inlineMotion = {
  motionName: "rc-menu-collapse",
  onAppearStart: collapseNode,
  onAppearActive: expandNode,
  onEnterStart: collapseNode,
  onEnterActive: expandNode,
  onLeaveStart: expandNode,
  onLeaveActive: collapseNode,
};

const StyledHeaderContainer = styled(Flex)`
  flex-direction: column;
  justify-content: center;
  flex-wrap: no-wrap;
  min-height: 5rem;
  position: sticky;
  top: 0;
  width: 100%;
  z-index: 1030;
  padding-right: 1.06rem;
  padding-left: 1.06rem;
  border-radius: 0 0 1.25rem 1.25rem;
  @media (min-width: 600px) {
    padding-right: 2rem;
    padding-left: 2rem;
  }
  @media (min-width: 974px) {
    padding-right: 1.5rem;
    padding-left: 1.5rem;
  }
  @media (min-width: 1157px) {
    padding-right: 2rem;
    padding-left: 3rem;
  }
  @media (min-width: 1440px) {
    padding-right: 6.5rem;
    padding-left: 4.5rem;
  }
`;

const StyledHeaderNav = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: nowrap;
  font-weight: 400;
  width: 100%; 
  py: 0.75rem;
  @media (min-width: 768px) {
    gap: 1rem;
  }
  @media (min-width: 974px) {
    gap: 1.5rem;
  }
  @media (min-width: 1224px) {
    gap: 4rem;
  }
`;

const burgerMenuStyles = {
  bmBurgerButton: {
    width: "1.813rem",
    height: "1.813rem",
    position: "relative",
    padding: 0,
    marginRight: -30,
  },
  bmCrossButton: {
    height: "32px",
    width: "32px",
    top: "1.5rem",
    right: "1rem",
  },
  bmCross: {
    height: "32px",
    width: "32px",
  },
  bmMenuWrap: {
    position: "fixed",
    height: "100%",
    width: "100%",
    backgroundColor: colors.white,
    top: 0,
  },
  bmMenu: {
    padding: "0 1rem",
    fontSize: "1.15em",
    height: "76%",
    width: "100%",
    backgroundColor: colors.white,
  },
  bmMorphShape: {
    fill: colors.primary100,
  },
  bmItemList: {
    color: colors.white,
  },
  bmItem: {
    display: "block",
  },
  bmOverlay: {
    background: colors.black,
    opacity: 0.4,
    overflow: "hidden",
  },
};

const promoStyles = {
  contentContainer: {
    maxWidth: "213px",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    "@media(max-width: 1669px)": {
      flexDirection: "row",
      alignItems: "flex-start",
      maxWidth: "100%",
    },
  },
};

const styledMenu = {
  display: "flex",
  alignItems: "center",
  backgroundColor: colors.white,
  gap: ["1rem", "1rem", "1rem", "0.1rem"],
};

const burgerMenuItemsStyles = {
  divider: {
    width: "100%",
    height: "1px",
    backgroundColor: colors.neutral200,
    my: "1rem",
  },
  promoSection: {
    position: "absolute",
    bottom: 0,
    p: "0rem",
    left: "1rem",
    right: "1rem",
    bottom: "2rem",
  },
  logoContainer: {
    display: "flex !important",
    position: "absolute",
    height: "5rem",
    left: "1rem",
    right: "1rem",
    width: "100%",
    top: 0,
    backgroundColor: colors.white,
    alignItems: "center",
  },
};

const PromoSection = ({ menuData, innerCustomStyles, textInnerContainerCustomStyles, textContainerCustomStyles, customStyles }) => {
  const handleClick = () => {
    window.open(menuData?.url, "newwindow");
  };
  return (
    <Flex
      sx={{
        py: "2.23rem",
        px: "3.16rem",
        backgroundColor: colors.white,
        ...customStyles,
      }}
    >
      <Flex sx={{...promoStyles.contentContainer, ...innerCustomStyles}}>
        {menuData?.image && (
          <GatsbyImage
            image={getImage(menuData?.image)}
            alt={menuData?.title}
            style={{ borderRadius: "0.4375rem", minWidth: "11.5rem" }}
          />
        )}
        <Flex
          sx={{
            flexDirection: "column",
            ml: 0,
            maxWidth: "15.688rem",
            "@media(max-width: 1669px)": {
              ml: "1.5rem",
            },
            "@media(max-width: 1030px)": {
              maxWidth: "100%"
            },
            ...textContainerCustomStyles
          }}
        >
          <Text
            sx={{
              ...typography.h6,
              mt: "1.56rem",
              mb: "0.5rem",
              "@media(max-width: 1669px)": {
                mt: 0,
              },
              ...textInnerContainerCustomStyles
            }}
          >
            {menuData?.title}
          </Text>
          <Text sx={{ ...typography.textMd400, mb: "1.56rem" }}>
            {menuData?.description}
          </Text>
          <NavLink
            onClick={handleClick}
            customStyles={{ color: colors.primaryMain, px: 0 }}
          >
            {menuData?.linkText}
          </NavLink>
        </Flex>
      </Flex>
    </Flex>
  );
};

const Logo = ({ region, isLogoClickable }) => {
  const regionLinks = {
    ME: "/middle-east/",
    "middle-east": "/middle-east/",
    ID: "/indonesia/",
    "indonesia": "/indonesia/",
    SG: "/singapore/",
    "singapore": "/singapore/",
    MY: "/malaysia/",
    "malaysia": "/malaysia/"
  };

  const homePageLink = regionLinks[region] || "/";

  return (
    <Link to={isLogoClickable && homePageLink}>
      <img
        sx={{ width: ["5rem", "5.625rem", "5.625rem", "7rem"] }}
        src={logo}
        alt="greytHR"
      />
    </Link>
  );
};

const getSignUpLink = (type, regionCode, path) => {
  const encoded_path = encodeURIComponent(path);
  const cta_name = type == "H" ? "gc_2001_001" : "gc_2001_005";
  const signupLinkSrc =
    regionCode === "ME" || regionCode === "middle-east"
      ? `/middle-east/signup/?gc_name=${cta_name}&gc_source=wgc%2F${encoded_path}&gc_index=0`
      : `/signup/?gc_name=${cta_name}&gc_source=wgc%2F${encoded_path}&gc_index=0`;
  return signupLinkSrc;
};

const HeaderCTA = ({ regionCode, demoBlockId, isHrGarden, headerCtaName, path }) => {
  const type = "H";
  if (isHrGarden)
    return (
      <Href href={"#hr-garden-form"}>
        <Button variant="filled" id="subscribe_btn">
          Subscribe
        </Button>
      </Href>
    );
  
  const signupLink = getSignUpLink(type, regionCode, path);
  const loginLink = regionCode === "ME" ? "/middle-east/login/" : "/login/";

  return (
    <Flex
      sx={{
        flexDirection: "row",
        alignSelf: "center",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: "1.5rem",
      }}
    >
      {demoBlockId && (
        <Link
          to={demoBlockId} 
          sx={{ 
            textDecoration: "none", 
            "&:hover": { textDecoration: "none" } 
            }}
        >
          <Text sx={{ ...typography.textMd500 }}>Request Demo</Text>
        </Link>
      )}
      {(regionCode === "IN" || regionCode === "ME")&&(
        <Link to={headerCtaName === "Login" ? loginLink : signupLink}>
          <Button variant="filled" id={headerCtaName} customStyles={{width: headerCtaName === "Login" ? "6.25rem":"12.5rem"}}>
            {headerCtaName}
          </Button>
        </Link>
      )}
    </Flex>
  );
};

const Nav = ({
  region,
  menuHeaderItems,
  showHeaderCTA = "true",
  menu,
  demoBlockId,
  headerCtaName,
  path,
  newFeatureMenuItem,
  newFeatureMenuItemID
}) => {


  return (
    <Flex
      sx={{
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
      }}
    >
      <Flex>
        <NavMenu
          region={region}
          menuHeaderItems={menuHeaderItems}
          menu={menu}
        />
        {newFeatureMenuItem && (
          <Href
            href={newFeatureMenuItem[0]?.url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              ...typography.h6,
              textDecoration: "none",
              maxWidth: "13.75rem",
              ml: [0, 0, 0, "2rem"],
              "&:hover": { color: colors.primaryMain, textDecoration: "none"},
            }}
            id={newFeatureMenuItemID}
          >
            <Flex
              sx={{
                justifyContent: "center",
                alignItems: "center",
                "@media screen and (min-width: 1030px) and (max-width: 1090px)": {
                  display: "none",
                },    
              }}
            >
              <Image
                src={newFeatureGIF}
                alt="new-feature-tag"
                sx={{
                  maxWidth: "3.75rem",
                  height: "1.52rem",
                  objectFit: "contain",
                  mr: "0.25rem",
                  flexShrink: 0, 
                }}
              />
              <Text sx={{ whiteSpace: "nowrap" }}>
                {newFeatureMenuItem[0]?.label}
              </Text>
            </Flex>
          </Href>
        )}
      </Flex>
      {showHeaderCTA && ( 
        <HeaderCTA
          regionCode={region}
          demoBlockId={demoBlockId}
          headerCtaName={headerCtaName}
          path={path}
        />
      )}
    </Flex>
  );
};

const NavMenu = ({ region, menuHeaderItems, menu }) => {
  const pageRegionCode = region || useSelector((state) => state.app.region);  
  return (
    <Menu
      mode="horizontal"
      sx={styledMenu}
      triggerSubMenuAction="hover"
      motion={inlineMotion}
    >
      {menuHeaderItems?.menu?.map(
        ({ title, path, regional, hide, items = [] }, index) => {
          const hasSubMenu = items && items.length > 0;
          const promoKey = title === "Resources" ? "Resource" : title;
          const promoData = menu.find((item) => item.region === region && item.menu === promoKey);
          if (hide) {
            return null;
          }
          const parentTitle = (
            <span
              sx={{
                ...typography.textMd500,
                "&:hover": { color: colors.primaryMain },
                mr: "1rem"
              }}
            >
              {title}
              <ChevronDown size="1.25rem" style={{ marginLeft: "0.5rem" }} />
            </span>
          );

          if (hasSubMenu) {
            return (
              <SubMenu
                title={parentTitle}
                key={`menu-items-${title}`}
                mode="horizontal"
              >
                <MenuItemGroup id="flexSubMenuGroup">
                  {items.map((item, index) => {
                    const hasColumns = item.column && item.column.length > 0;

                    if (hasColumns && item.column[0].title === "====") {
                      return (
                        <Divider
                          key={`menu-items-${title}`}
                          className="customDivider"
                        />
                      );
                    }
                    if (hasColumns) {
                      return (
                        <MenuItemGroup
                          className="columnMenuGroup"
                          key={`column-menu-${index}`}
                          style={{ padding: "1.23rem 2.93rem 0rem 1.81rem" }}
                        >
                          {(item.category && item.category !== "Competitors") && (
                            <Box
                              sx={{
                                ...typography.h6,
                                py: "1rem",
                                px: "0.75rem",
                              }}
                            >
                              {item.category}
                            </Box>
                          )}
                          {item.column.map(
                            (
                              {
                                title,
                                path,
                                regional,
                                iconName,
                                subContent = [],
                                imageUrl,
                              },
                              index
                            ) => {
                              const hasSubContent =
                                subContent && subContent.length > 0;

                              if (title === "---") {
                                return (
                                  <Divider
                                    key={`divider-column-menu-${index}`}
                                    sx={{
                                      marginRight: "-2.93rem",
                                      height: 220,
                                    }}
                                  />
                                );
                              }
                              if (hasSubContent) {
                                return (
                                  <MenuItemGroup
                                    id="menuGroup"
                                    key={`menu-group-${index}`}
                                  >
                                    <MenuItem>
                                      <HeaderLink
                                        to={path}
                                        key={path}
                                        title={title}
                                        region={pageRegionCode}
                                        regional={regional}
                                        iconName={iconName}
                                        isIconRequired={true}
                                      />
                                    </MenuItem>
                                    {/* nested menu-item */}
                                    {subContent.map(
                                      ({ title, path, regional }) => (
                                        <MenuItem key={`sub-menu-${title}`}>
                                          <div
                                            sx={{
                                              ml: "2.62rem",
                                              border: `0.5px solid ${colors.white} `,
                                            }}
                                          >
                                            <HeaderLink
                                              to={path}
                                              key={path}
                                              title={title}
                                              region={pageRegionCode}
                                              regional={regional}
                                            />
                                          </div>
                                        </MenuItem>
                                      )
                                    )}
                                  </MenuItemGroup>
                                );
                              }

                              return (
                                <MenuItem key={`${title}${index}`}>
                                  <HeaderLink
                                    to={path}
                                    key={path}
                                    title={title}
                                    region={pageRegionCode}
                                    regional={regional}
                                    iconName={iconName}
                                    isIconRequired={true}
                                    imageUrl={imageUrl}
                                  />
                                </MenuItem>
                              );
                            }
                          )}
                        </MenuItemGroup>
                      );
                    }
                  })}
                    <PromoSection
                      menuData={promoData}
                      customStyles={{
                        display: "flex",
                        "@media(max-width: 1669px)": {
                          display: "none",
                        },
                        marginLeft: title === "Resources" ? "-3.265rem" : 0,
                      }}
                    />
                </MenuItemGroup>
                    <PromoSection
                      menuData={promoData}
                      customStyles={{
                        display: "none",
                        "@media(max-width: 1669px)": {
                          display: "flex",
                        },
                      }}
                    />
              </SubMenu>
            );
          }
          //  if menu has no submenu item
          return (
            <MenuItem 
              key={index}
            >
              <Box
                sx={{
                  '@media (min-width: 1030px) and (max-width: 1400px)': {
                    display: title==="Customers" ? 'none' : 'block',
                  },
                  mr: "1rem",
                }} 
              >
                <HeaderLink
                  to={path}
                  key={path}
                  title={title}
                  region={pageRegionCode}
                  regional={regional}
                  isNavItem
                />
              </Box>
            </MenuItem>
          );
        }
      )}
    </Menu>
  );
};

const HrGardenNavMenu = ({ region, menuHeaderItems}) => {
  const pageRegionCode = region || useSelector((state) => state.app.region);
  return (
    <Flex sx={{ justifyContent: "space-between" }}>
      <Menu
        mode="horizontal"
        sx={styledMenu}
        triggerSubMenuAction="hover"
        motion={inlineMotion}
      >
        {menuHeaderItems?.menu?.map(({ title, hide, items = [] }) => {
          if (hide) {
            return null;
          }

          const hasSubMenu = items && items.length > 0;
          const parentTitle = (
            <span
              sx={{
                ...typography.textMd500,
                "&:hover": { color: colors.primaryMain },
                '@media(min-width: 1065px)': {
                  mr: "1.5rem" 
                },
              }}
            >
              {title}
              <ChevronDown size="1.25rem" style={{ marginLeft: "0.5rem" }} />
            </span>
          );

          if (hasSubMenu) {
            return (
              <SubMenu title={parentTitle} mode="horizontal">
                <MenuItemGroup
                  id="flexSubMenuGroup"
                  style={{ width: '21.063rem' }}
                >
                  {items.map((item) => {
                    const hasColumns = item.column && item.column.length > 0;

                    if (hasColumns) {
                      return (
                        <MenuItemGroup
                          className="columnMenuGroup"
                          style={{ 
                            padding: "1.188rem 1.92rem 0.375rem 1.859rem",
                            width: '17.438rem',
                          }}
                        >
                          {item.column.map(
                            (
                              {
                                title,
                                path,
                                regional,
                                iconName,
                                imageUrl,
                              },
                              index
                            ) => {
                              return (
                                <MenuItem
                                  key={`${title}${index}`}
                                  style={{
                                    width: '17.438rem',
                                  }}
                                  sx={{
                                    "&:hover": {
                                      backgroundColor: colors.hoverBackground,
                                    },
                                  }}
                                >
                                  <HeaderLink
                                    to={path}
                                    key={path}
                                    title={title}
                                    region={pageRegionCode}
                                    regional={regional}
                                    iconName={iconName}
                                    isIconRequired={true}
                                    imageUrl={imageUrl}
                                  />
                                </MenuItem>
                              );
                            }
                          )}
                        </MenuItemGroup>
                      );
                    }
                  })}
                </MenuItemGroup>
              </SubMenu>
            );
          }
        })}
      </Menu>
      <Link 
        href={"#hr-garden-form"}
        sx={{
          display: 'block',
          '@media screen and (min-width: 1000px) and (max-width: 1500px)': {
            display: 'none',
          },
        }}
      >
        <Button variant="filled" id="subscribe_btn">
          Subscribe
        </Button>
      </Link>
    </Flex>
  );
};

const SubMenuCard = ({ title }) => {
  const [theme, setTheme] = useState(colors.black);
  const changeColor = () => {
    if (theme === colors.black) {
      setTheme(colors.primaryMain);
    } else {
      setTheme(colors.black);
    }
  };
  return (
    <Flex
      sx={{
        py: "1rem",
        px: "0.75rem",
        gap: "1rem",
        alignItems: "center",
        color: theme,
      }}
      onClick={changeColor}
    >
      <Text
        sx={{
          ...typography.textMd500,
          ml: "0.69rem",
          color: theme,
        }}
      >
        {title}
      </Text>
      <ChevronRight size="1.25rem" color={theme} />
    </Flex>
  );
};

const BurgerMenuItems = ({ px, menuHeaderItems, region }) => {
  const pageRegionCode = region || useSelector((state) => state.app.region);
  return menuHeaderItems?.menu?.map(
    ({ title, path, regional = false, hide, items = [] }, index) => {
      const hasSubMenu = items && items.length > 0;
      if (hide === true) {
        return null;
      }
      if (hasSubMenu) {
        return (
          <Box key={`menu-${index}`}>
            <Accordion as="h3" key={index}>
              <Box
                label={title}
                isElement={true}
                Element={() => <SubMenuCard title={title} />}
                key={uid(title)}
                isOpen={false}
                style={{
                  px: 0,
                  m: 0,
                  py: 0,
                }}
              >
                {items.map((item, i) => {
                  const hasColumns = item.column && item.column.length > 0;
                  if (hasColumns) {
                    return (
                      <Box key={`submenu-${i}`}>
                        {item.category && (
                          <Box
                            sx={{
                              ...typography.h6,
                              py: "1rem",
                              px: "0.75rem",
                              mx: "0.625rem",
                            }}
                          >
                            {item.category}
                          </Box>
                        )}
                        {item.column.map(
                          (
                            {
                              title,
                              path,
                              subContent = [],
                              show,
                              iconName,
                              imageUrl,
                            },
                            index
                          ) => {
                            const hasSubContent =
                              subContent && subContent.length > 0;
                            if (title !== "====") {
                              return (
                                <Box key={"sub-menu-" + index}>
                                  <HeaderLink
                                    to={path}
                                    region={pageRegionCode}
                                    regional={regional}
                                    key={path}
                                    title={title}
                                    px={10}
                                    vertical
                                    isIconRequired={true}
                                    iconName={iconName}
                                    isMobile
                                    imageUrl={imageUrl}
                                  />
                                  {hasSubContent
                                    ? subContent.map((c) => (
                                        <HeaderLink
                                          to={c.path}
                                          region={pageRegionCode}
                                          regional={c.regional}
                                          key={c.path}
                                          title={c.title}
                                          px={4}
                                          vertical
                                        />
                                      ))
                                    : null}
                                </Box>
                              );
                            }
                          }
                        )}{" "}
                      </Box>
                    );
                  }
                })}
              </Box>
            </Accordion>
            {index != 4 && <Box sx={burgerMenuItemsStyles.divider} />}
          </Box>
        );
      }
      return (
        <Box key={`menu-${index}`}>
          <HeaderLink
            to={path}
            region={pageRegionCode}
            regional={regional}
            key={path}
            title={title}
            px={px}
            vertical
            color={colors.black}
          />
          {index != 4 && <Box sx={burgerMenuItemsStyles.divider} />}
        </Box>
      );
    }
  );
};

const BurgerMenu = ({ menuHeaderItems, region, menu, isHrGarden }) => {
  if (isHrGarden)
    return <HrGardenBurgerMenu menuHeaderItems={menuHeaderItems} region={region} />;
  const promoData = menu.find((item) => item.region === region && item.menu === "Product");
  return (
    <div sx={{ p: 9, mx: 3 }}>
      {!(region==="ID" || region==="SG" || region==="MY") && (
      <SlideMenu
        right
        styles={burgerMenuStyles}
        customBurgerIcon={
          <img
            src={burgerMenuIcon}
            alt="burger-menu"
            sx={{ width: "1.813rem" }}
          />
        }
        customCrossIcon={
          <img src={crossIcon} alt="cross-menu" sx={{ width: "32px" }} />
        }
      >
        <Flex style={burgerMenuItemsStyles.logoContainer}>
          <Logo region={region} />
        </Flex>
        <Flex style={{ marginTop: "5.81rem", justifyContent: "space-between" }}>
          <BurgerMenuItems menuHeaderItems={menuHeaderItems} region={region} />
          {promoData && (
            <PromoSection
              menuData={promoData}
              customStyles={burgerMenuItemsStyles.promoSection}
            />
          )}
        </Flex>
      </SlideMenu>
      )}
    </div>
  );
};

const HrGardenBurgerMenu = ({ menuHeaderItems, region }) => {
  return (
    <div sx={{ p: 9, mx: 3 }}>
      <SlideMenu
        right
        styles={{ ...burgerMenuStyles, bmMenu: { height: "100%" } }}
        customBurgerIcon={
          <img
            src={burgerMenuIcon}
            alt="burger-menu"
            sx={{ width: "1.813rem" }}
          />
        }
        customCrossIcon={
          <img src={crossIcon} alt="cross-menu" sx={{ width: "32px" }} />
        }
      >
        <Flex style={burgerMenuItemsStyles.logoContainer}>
          <Logo region={region} />
        </Flex>
        <Flex style={{ marginTop: "5.81rem", justifyContent: "space-between" }}>
          <BurgerMenuItems menuHeaderItems={menuHeaderItems} />
        </Flex>
      </SlideMenu>
    </div>
  );
};

const HeaderLink = ({
  to,
  title,
  px,
  region,
  regional = false,
  vertical = false,
  isIconRequired = false,
  iconName,
  isNavItem,
  isMobile = false,
  imageUrl
}) => {
  const sx = {
    px,
    pb: 0,
    width: ["100%", "100%", "auto", "auto"],
    "&:hover": {
      textDecoration: "none",
    }
  };
  if (vertical) {
    sx.display = "block";
  }
  return to && to.split("/")[0] === "https:" ? (
    <Href
      href={to}
      target="blank"
      style={{
        paddingLeft: isMobile ? "10px" : 0,
        textDecoration: "none",
        ...sx,
      }}
    >
      <ListCard
        iconName={isIconRequired ? iconName : null}
        title={title}
        isChevronRequired={false}
        hoverTextColor={colors.primaryMain}
        hoverBgColor={colors.primary100}
        imageUrl={imageUrl}
        customStyles={{
          minWidth: "17.4375rem",
        }}
      />
    </Href>
  ) : (
    <RegionalLink
      sx={sx}
      to={to}
      region={region}
      regional={regional}
      variant="header"
    >
      {isNavItem ? (
        <span
          sx={{
            ...typography.textMd500,
            color: colors.black,
            "&:hover": { color: colors.primaryMain },
          }}
        >
          {title}
        </span>
      ) : (
        <ListCard
          iconName={isIconRequired ? iconName : null}
          title={title}
          isChevronRequired={false}
          hoverTextColor={colors.primaryMain}
          hoverBgColor={colors.primary100}
          imageUrl={imageUrl}
        />
      )}
    </RegionalLink>
  );
};

const MobileMenuHolder = ({ menu, region, demoBlockId, isHrGarden, showMobileMenu, headerCtaName, path }) => {
  if(!showMobileMenu) return;
  const signupLink = getSignUpLink("V", region, path);
  return (
    <Flex sx={{ justifyContent: "flex-end" }}>
      <Box sx={{ display: ["none", "none", "flex", "flex"] }}>
        <HeaderCTA
          regionCode={region}
          demoBlockId={demoBlockId}
          isHrGarden={isHrGarden}
          headerCtaName={headerCtaName}
          path={path}
        />
      </Box>
      <Box sx={{ display: ["flex", "flex", "none", "none"] }}>
        {isHrGarden ? (
          <Href href={"#hr-garden-form"}>
            <Button variant="filled" id="header_signup_btn">
              Subscribe
            </Button>
          </Href>
        ) : (
          !(region === "ID" || region === "SG" || region === "MY") && (
            <Link to={signupLink}>
              {path !== "/" && (
                <Button variant="filled" id="header_signup_btn">
                  FREE TRIAL
                </Button>
              )}
            </Link>
          )
        )}
      </Box>
      {menu}
    </Flex>
  );
};

const Header = ({
  region,
  menuHeaderItems,
  menuItems,
  showHeaderCTA,
  demoBlockId,
  isHrGarden,
  isMenuRequired=true,
  headerCtaName='Start My FREE Trial',
  newFeatureMenuItem,
  newFeatureMenuItemID
}) => {
  const path = history.location.pathname;
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const bpIndex = useBreakpointIndex({ defaultIndex: 1 });
  region = region || useSelector((state) => state.app.region);
  menuItems = menuItems ?? ( menuHeaderItems ? menuHeaderItems[region] : null);

  const {
    data: { nodes },
  } = useStaticQuery(graphql`
    {
      data: allDatoCmsMenuPromoSection(filter: { isCurrent: { eq: true } }) {
        nodes {
          menu
          region
          title
          url
          description
          image {
            alt
            gatsbyImageData(width: 213, layout: CONSTRAINED, placeholder: BLURRED)
          }
          linkText
        }
      }
    }
  `);
  useEffect(() => {
    const updateShowMobileMenu = () => {
      setShowMobileMenu(window.innerWidth <= 1030);
    };
    updateShowMobileMenu();
    window.addEventListener("resize", updateShowMobileMenu);

    return function cleanup() {
      window.removeEventListener("resize", updateShowMobileMenu);
    };
  }, []);


  const shouldRenderMenu = !(
    path === "/signup/" ||
    path === "/signup" ||
    path === "/free-signup/" ||
    path === "/free-signup" ||
    path === "/email-verify" ||
    path === "/email-verify/"
  );

  const mobileMenu = (
    <BurgerMenu
      region={region}
      menuHeaderItems={menuItems}
      showHeaderCTA={showHeaderCTA}
      menu={nodes}
      isHrGarden={isHrGarden}
    />
  );
  return (
    <StyledHeaderContainer
      style={{
        backgroundColor: colors.white,
      }}
      as="div"
    >
      <StyledHeaderNav as="nav">
        <Logo region={region} isLogoClickable={isMenuRequired} />
        {isMenuRequired && shouldRenderMenu && (
          <Box sx={{ width: "100%" }}>
            {showMobileMenu || bpIndex <= 2 ? (
              <MobileMenuHolder
                shouldRenderMenu
                menu={mobileMenu}
                region={region}
                demoBlockId={demoBlockId}
                isHrGarden={isHrGarden}
                showMobileMenu={showMobileMenu}
                headerCtaName={headerCtaName}
                path={path}
              />
            ) : (
              bpIndex > 2 && (
                <Box>
                  {isHrGarden ? (
                    <HrGardenNavMenu
                      region={region}
                      menuHeaderItems={menuItems}
                      showHeaderCTA={showHeaderCTA}
                      menu={nodes}
                      demoBlockId={demoBlockId}
                    />
                  ) : (
                    <Nav
                      region={region}
                      menuHeaderItems={menuItems}
                      showHeaderCTA={showHeaderCTA}
                      menu={nodes}
                      demoBlockId={demoBlockId}
                      headerCtaName={headerCtaName}
                      path={path}
                      newFeatureMenuItem={newFeatureMenuItem}
                      newFeatureMenuItemID={newFeatureMenuItemID}
                    />
                  )}
                </Box>
              )
            )}
          </Box>
        )}
      </StyledHeaderNav>
    </StyledHeaderContainer>
  );
};


export default Header;
